import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { getAuthRedirect } from '@/utils/authRedirect';

/** Full-screen spinner shown while a session restore runs. */
function BootstrapSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

/**
 * 用户认证守卫：未登录重定向到登录页，保留原始 URL
 *
 * Reads the customer session only; an admin session in the same browser
 * does not count as signed in here.
 */
export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isBootstrapping } = useAuthStore();
  const location = useLocation();

  // Wait for the silent refresh to resolve before deciding (req 4.5):
  // on a page reload the access token is restored from the httpOnly cookie.
  if (isBootstrapping) {
    return <BootstrapSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }
  return <>{children}</>;
}

/**
 * Guest-only guard: signed-in customers continue to the intended destination.
 * Reads the customer session only, like {@link AuthGuard}.
 */
export function GuestGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isBootstrapping } = useAuthStore();
  const location = useLocation();

  if (isBootstrapping) {
    return <BootstrapSpinner />;
  }

  if (isAuthenticated) {
    const redirectTo = getAuthRedirect(location.search, location.state);
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

/** 管理员认证守卫：验证管理员身份（只读取管理员会话） */
export function AdminGuard({ children }: { children: React.ReactNode }) {
  const { isAdminAuthenticated, isAdminBootstrapping } = useAuthStore();

  if (isAdminBootstrapping) {
    return <BootstrapSpinner />;
  }

  if (!isAdminAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  return <>{children}</>;
}

/** 管理员权限守卫：验证具体权限（只读取管理员会话） */
export function PermissionGuard({
  permission,
  children,
  fallback,
}: {
  permission: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const { adminPermissions } = useAuthStore();

  if (!adminPermissions.includes(permission)) {
    return fallback ? <>{fallback}</> : null;
  }
  return <>{children}</>;
}
